import { StringUtility } from '@common/utils';
import { DateTime } from 'luxon';
import { WellnessExamPrognosisScore } from './Enums';
import { ISyncModel, SyncModel } from '@common/models/SyncModel';
import {
  IPrognosisScoreModel,
  PrognosisScoreModel,
} from './PrognosisScoreModel';
import { makeObservable, observable } from 'mobx';

export interface IVisitWellnessExamModel extends ISyncModel {
  visitWellnessExamId: number;
  vaccinationsRequested: boolean | undefined;
  diagnosticRequested: boolean | undefined;
  parasiticalRequested: boolean | undefined;
  microchipRequested: boolean | undefined;
  isHealthy: boolean | undefined;
  hasNormalAppetite: boolean | undefined;
  isBehavorActive: boolean | undefined;
  hasNoIllnessSigns: boolean | undefined;
  temperature: number | undefined;
  pulse: number | undefined;
  weight: number | undefined;
  respiration: number | undefined;
  approvedForVaccinations: boolean | undefined;
  notes: string;
  temperatureUTE: boolean | undefined;
  temperatureDNE: boolean | undefined;
  pulseUTE: boolean | undefined;
  pulseDNE: boolean | undefined;
  respirationUTE: boolean | undefined;
  respirationDNE: boolean | undefined;
  visitDate: string | undefined;
  attitude: string | undefined;
  painScale: number | undefined;
  mucousMembrane: string | undefined;
  capillaryRefill: string | undefined;
  bodyConditionScore: number | undefined;
  attitudeUTE: boolean | undefined;
  attitudeDNE: boolean | undefined;
  painScaleUTE: boolean | undefined;
  painScaleDNE: boolean | undefined;
  mucousMembraneUTE: boolean | undefined;
  mucousMembraneDNE: boolean | undefined;
  capillaryRefillUTE: boolean | undefined;
  capillaryRefillDNE: boolean | undefined;
  bodyConditionScoreUTE: boolean | undefined;
  bodyConditionScoreDNE: boolean | undefined;
  intakeNotes: string | undefined;
  subjectiveNotes: string | undefined;
  objectiveNotes: string | undefined;
  vitalsNotes: string | undefined;
  planNotes: string | undefined;
  diagnosisNotes: string | undefined;
  prognosisNotes: string | undefined;
  dischargeNotes: string | undefined;
  privateNotes: string | undefined;
  prognosisScore: IPrognosisScoreModel | undefined;
  prognosisScoreId: number | undefined;
  diagnosisIds: number[] | undefined;
  hasEarIssue: boolean | undefined;
  hasChronicIssue: boolean | undefined;
  hasSkinAllergies: boolean | undefined;
  hasVomittingDiarrhea: boolean | undefined;
}

export class VisitWellnessExamModel
  extends SyncModel
  implements Record<keyof IVisitWellnessExamModel, any>
{
  @observable visitWellnessExamId: number;
  @observable vaccinationsRequested: boolean | undefined;
  @observable diagnosticRequested: boolean | undefined;
  @observable parasiticalRequested: boolean | undefined;
  @observable microchipRequested: boolean | undefined;
  @observable isHealthy: boolean | undefined;
  @observable hasNormalAppetite: boolean | undefined;
  @observable isBehavorActive: boolean | undefined;
  @observable hasNoIllnessSigns: boolean | undefined;
  @observable temperature: number | undefined;
  @observable pulse: number | undefined;
  @observable weight: number | undefined;
  @observable respiration: number | undefined;
  @observable approvedForVaccinations: boolean | undefined | null;
  @observable notes: string | undefined; // assessment notes
  @observable temperatureUTE: boolean | undefined;
  @observable temperatureDNE: boolean | undefined;
  @observable pulseUTE: boolean | undefined;
  @observable pulseDNE: boolean | undefined;
  @observable respirationUTE: boolean | undefined;
  @observable respirationDNE: boolean | undefined;
  @observable visitDate: DateTime | undefined;
  @observable attitude: string | undefined;
  @observable painScale: number | undefined;
  @observable mucousMembrane: string | undefined;
  @observable capillaryRefill: string | undefined;
  @observable bodyConditionScore: number | undefined;

  @observable attitudeUTE: boolean | undefined;
  @observable attitudeDNE: boolean | undefined;
  @observable painScaleUTE: boolean | undefined;
  @observable painScaleDNE: boolean | undefined;
  @observable mucousMembraneUTE: boolean | undefined;
  @observable mucousMembraneDNE: boolean | undefined;
  @observable capillaryRefillUTE: boolean | undefined;
  @observable capillaryRefillDNE: boolean | undefined;
  @observable bodyConditionScoreUTE: boolean | undefined;
  @observable bodyConditionScoreDNE: boolean | undefined;

  @observable intakeNotes: string | undefined;
  @observable subjectiveNotes: string | undefined;
  @observable objectiveNotes: string | undefined;
  @observable vitalsNotes: string | undefined;
  @observable planNotes: string | undefined;
  @observable diagnosisNotes: string | undefined;
  @observable prognosisNotes: string | undefined;
  @observable dischargeNotes: string | undefined;
  @observable privateNotes: string | undefined;

  @observable prognosisScore: PrognosisScoreModel | undefined;
  @observable prognosisScoreId: WellnessExamPrognosisScore | undefined;

  @observable diagnosisIds: number[] | undefined;
  @observable hasEarIssue: boolean | undefined;
  @observable hasChronicIssue: boolean | undefined;
  @observable hasSkinAllergies: boolean | undefined;
  @observable hasVomittingDiarrhea: boolean | undefined;

  constructor(params: Partial<IVisitWellnessExamModel>) {
    super(params);
    this.visitWellnessExamId = params.visitWellnessExamId ?? 0;
    this.vaccinationsRequested = StringUtility.SanitizeBool(
      params.vaccinationsRequested,
    );
    this.diagnosticRequested = StringUtility.SanitizeBool(
      params.diagnosticRequested,
    );
    this.parasiticalRequested = StringUtility.SanitizeBool(
      params.parasiticalRequested,
    );
    this.microchipRequested = StringUtility.SanitizeBool(
      params.microchipRequested,
    );
    this.isHealthy = StringUtility.SanitizeBool(params.isHealthy);
    this.hasNormalAppetite = StringUtility.SanitizeBool(
      params.hasNormalAppetite,
    );
    this.isBehavorActive = StringUtility.SanitizeBool(params.isBehavorActive);
    this.hasNoIllnessSigns = StringUtility.SanitizeBool(
      params.hasNoIllnessSigns,
    );
    this.temperature = StringUtility.SanitizeNumber(params.temperature);
    this.pulse = StringUtility.SanitizeNumber(params.pulse);
    this.weight = StringUtility.SanitizeNumber(params.weight);
    this.respiration = StringUtility.SanitizeNumber(params.respiration);
    this.approvedForVaccinations = StringUtility.SanitizeBool(
      params.approvedForVaccinations,
    );
    this.notes = params.notes ?? undefined;
    this.temperatureUTE = StringUtility.SanitizeBool(params.temperatureUTE);
    this.temperatureDNE = StringUtility.SanitizeBool(params.temperatureDNE);
    this.pulseUTE = StringUtility.SanitizeBool(params.pulseUTE);
    this.pulseDNE = StringUtility.SanitizeBool(params.pulseDNE);
    this.respirationUTE = StringUtility.SanitizeBool(params.respirationUTE);
    this.respirationDNE = StringUtility.SanitizeBool(params.respirationDNE);
    this.visitDate = StringUtility.SanitizeDateTimeISOAsUtc(params.visitDate);
    this.attitude = StringUtility.SanitizeString(params.attitude);
    this.painScale = StringUtility.SanitizeNumber(params.painScale);
    this.mucousMembrane = StringUtility.SanitizeString(params.mucousMembrane);
    this.capillaryRefill = StringUtility.SanitizeString(params.capillaryRefill);
    this.bodyConditionScore = StringUtility.SanitizeNumber(
      params.bodyConditionScore,
    );
    this.attitudeUTE = StringUtility.SanitizeBool(params.attitudeUTE);
    this.attitudeDNE = StringUtility.SanitizeBool(params.attitudeDNE);
    this.painScaleUTE = StringUtility.SanitizeBool(params.painScaleUTE);
    this.painScaleDNE = StringUtility.SanitizeBool(params.painScaleDNE);
    this.mucousMembraneUTE = StringUtility.SanitizeBool(
      params.mucousMembraneUTE,
    );
    this.mucousMembraneDNE = StringUtility.SanitizeBool(
      params.mucousMembraneDNE,
    );
    this.capillaryRefillUTE = StringUtility.SanitizeBool(
      params.capillaryRefillUTE,
    );
    this.capillaryRefillDNE = StringUtility.SanitizeBool(
      params.capillaryRefillDNE,
    );
    this.bodyConditionScoreUTE = StringUtility.SanitizeBool(
      params.bodyConditionScoreUTE,
    );
    this.bodyConditionScoreDNE = StringUtility.SanitizeBool(
      params.bodyConditionScoreDNE,
    );
    this.intakeNotes = StringUtility.SanitizeString(params.intakeNotes);
    this.subjectiveNotes = StringUtility.SanitizeString(params.subjectiveNotes);
    this.objectiveNotes = StringUtility.SanitizeString(params.objectiveNotes);
    this.vitalsNotes = StringUtility.SanitizeString(params.vitalsNotes);
    this.planNotes = StringUtility.SanitizeString(params.planNotes);
    this.diagnosisNotes = StringUtility.SanitizeString(params.diagnosisNotes);
    this.prognosisNotes = StringUtility.SanitizeString(params.prognosisNotes);
    this.dischargeNotes = StringUtility.SanitizeString(params.dischargeNotes);
    this.privateNotes = StringUtility.SanitizeString(params.privateNotes);
    this.prognosisScore = params.prognosisScore
        ? new PrognosisScoreModel(params.prognosisScore)
        : undefined;
    this.prognosisScoreId = StringUtility.SanitizeNumber(
      params.prognosisScoreId,
    );
    this.diagnosisIds = params.diagnosisIds ?? undefined;
    this.hasEarIssue = StringUtility.SanitizeBool(params.hasEarIssue);
    this.hasChronicIssue = StringUtility.SanitizeBool(params.hasChronicIssue);
    this.hasVomittingDiarrhea = StringUtility.SanitizeBool(
      params.hasVomittingDiarrhea,
    );
    this.hasSkinAllergies = StringUtility.SanitizeBool(params.hasSkinAllergies);

    makeObservable(this);
  }
}
